import { UserCheck } from 'lucide-react';
import { Badge, Button, EmptyState, Text } from '../atoms';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section/Section';
import { AdminTable } from './AdminTable';
import type { AdminConfirmRequest } from './AdminConfirmDialog';
import { formatDate } from './adminFormat';
import { usePendingApprovals } from './usePendingApprovals';
import { approveUser, rejectUser, type AdminUser } from '../../services/adminService';
import { useTranslation } from '../../i18n/useTranslation';

export interface PendingApprovalsProps {
  /**
   * Opens the page's confirmation dialog for an action (AdminUserManagement's
   * `ask`): title/message/label, the action, and the "done" announcement.
   */
  ask: (request: Omit<AdminConfirmRequest, 'onConfirm'>, action: () => Promise<void>, doneMessage: string) => void;
}

function fullName(user: AdminUser): string {
  return [user.first_name ?? '', user.last_name ?? ''].join(' ').trim();
}

/**
 * Admin → Benutzer, section "Freigabe ausstehend" (closed beta): every account
 * that confirmed its e-mail address and waits for approval — name, e-mail,
 * registration date and the e-mail choices made at sign-up — with
 * "Freischalten" / "Ablehnen", both confirmed in a dialog.
 */
export function PendingApprovals({ ask }: PendingApprovalsProps) {
  const { t, i18n } = useTranslation();
  const { users, count, isLoading, isError, isRefetching, refetch } = usePendingApprovals();

  function consentLabel(user: AdminUser) {
    const consents = user.email_consents;
    const items: string[] = [];
    if (consents?.product_updates) items.push(t('adminArea.users.approval.consentProductUpdates'));
    if (consents?.task_reminders) items.push(t('adminArea.users.approval.consentTaskReminders'));
    return items.length > 0 ? items.join(', ') : t('adminArea.users.approval.consentNone');
  }

  return (
    <Section title={t('adminArea.users.approval.title', { count })} id="pending-approvals">
      <Text className="rg-admin-table__muted">{t('adminArea.users.approval.description')}</Text>

      {isLoading && (
        <LiveRegion>
          <Text>{t('adminArea.users.approval.loading')}</Text>
        </LiveRegion>
      )}

      {isError && (
        <LoadErrorState
          title={t('adminArea.users.approval.loadError')}
          onRetry={() => void refetch()}
          retrying={isRefetching}
          titleLevel={3}
        />
      )}

      {!isLoading && !isError && users.length === 0 && (
        <EmptyState
          icon={<UserCheck size={48} strokeWidth={1.5} aria-hidden="true" />}
          headline={t('adminArea.users.approval.emptyTitle')}
          description={t('adminArea.users.approval.emptyMessage')}
          actionLabel={t('adminArea.reload')}
          onAction={() => void refetch()}
          headingLevel={3}
        />
      )}

      {!isLoading && !isError && users.length > 0 && (
        <AdminTable caption={t('adminArea.users.approval.tableCaption')}>
          <thead>
            <tr>
              <th scope="col">{t('adminArea.users.approval.col.name')}</th>
              <th scope="col">{t('adminArea.users.col.email')}</th>
              <th scope="col">{t('adminArea.users.approval.col.registeredAt')}</th>
              <th scope="col">{t('adminArea.users.approval.col.emailConsents')}</th>
              <th scope="col">{t('adminArea.users.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const name = fullName(user) || user.email;
              const email = user.email;
              return (
                <tr key={user.id}>
                  <th scope="row">
                    {name}
                    <Badge tone="warning" className="rg-admin-table__inline-badge">
                      {t('adminArea.users.approval.statusPending')}
                    </Badge>
                  </th>
                  <td>{email}</td>
                  <td className="rg-admin-table__numeric">{formatDate(user.created_at, i18n.language)}</td>
                  <td>{consentLabel(user)}</td>
                  <td>
                    <div className="rg-admin-table__actions">
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={t('adminArea.users.approval.approveFor', { email })}
                        onClick={() =>
                          ask(
                            {
                              title: t('adminArea.users.approval.approve'),
                              message: t('adminArea.users.approval.confirmApprove', { email }),
                              confirmLabel: t('adminArea.users.approval.approve'),
                            },
                            () => approveUser(user.id),
                            t('adminArea.users.approval.done.approved', { email }),
                          )
                        }
                      >
                        {t('adminArea.users.approval.approve')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('adminArea.users.approval.rejectFor', { email })}
                        onClick={() =>
                          ask(
                            {
                              title: t('adminArea.users.approval.reject'),
                              message: t('adminArea.users.approval.confirmReject', { email }),
                              confirmLabel: t('adminArea.users.approval.reject'),
                            },
                            () => rejectUser(user.id),
                            t('adminArea.users.approval.done.rejected', { email }),
                          )
                        }
                      >
                        {t('adminArea.users.approval.reject')}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </AdminTable>
      )}
    </Section>
  );
}

export default PendingApprovals;
